// Kitchen flow pilot (Batch 5): a searchable item picker for the PO lines,
// replacing a native <select> over ~500 items. Type part of the name or code;
// ↑/↓ move, Enter picks, Esc closes. A USB scanner is handled by the dialog
// (useItemScanner adds the scanned item as a line), as in BoughtItemsEditor.
import { useId, useMemo, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { searchItems } from './purchasingPilotLogic'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.picker

export interface PickerItem {
  id: string
  name: string
  unit: string
  code?: string | null
}

export function ItemSearchPicker({ items, value, onChange, ariaLabel }: {
  items: PickerItem[]
  value: string
  onChange: (id: string) => void
  ariaLabel?: string
}) {
  const selected = items.find((i) => i.id === value)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const matches = useMemo(() => searchItems(items, query, 20), [items, query])
  const listId = useId()

  const pick = (id: string) => {
    onChange(id)
    setQuery('')
    setOpen(false)
  }

  return (
    <div className="relative">
      <Input
        role="combobox"
        aria-label={ariaLabel ?? S.placeholder}
        aria-expanded={open && query.trim() !== ''}
        aria-controls={listId}
        aria-autocomplete="list"
        placeholder={selected ? selected.name : S.placeholder}
        value={open ? query : (selected ? `${selected.name} (${selected.unit})` : '')}
        onFocus={() => { setOpen(true); setQuery(''); setActive(0) }}
        onBlur={() => setOpen(false)}
        onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true) }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((a) => Math.min(a + 1, Math.max(matches.length - 1, 0)))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter') {
            if (matches[active]) {
              e.preventDefault()
              pick(matches[active].id)
            }
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      {open && query.trim() !== '' && (
        <ul id={listId} role="listbox" className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border bg-popover text-popover-foreground shadow-md">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">{S.none}</li>
          ) : matches.map((m, i) => (
            <li
              key={m.id}
              role="option"
              aria-selected={i === active}
              // mousedown, not click: picking must not blur the input first.
              onMouseDown={(e) => { e.preventDefault(); pick(m.id) }}
              onMouseEnter={() => setActive(i)}
              className={cn('cursor-pointer px-3 py-2 text-sm', i === active && 'bg-muted')}
            >
              {m.name} <span className="text-muted-foreground">({m.unit})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
